import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import {
  networkConnectionsQuery,
  networkCountsQuery,
  networkLatencyQuery,
  networkStatisticsQuery,
  networkStatusQuery,
  networkWatch,
  type NetworkPeer,
  type NetworkPortCard,
} from '../api/devices'
import { Spinner } from '../ui/Spinner'
import { bytesLabel, lastOf, NetworkPieChart, NetworkSplineChart } from './NetworkCharts'
import type { ScreenProps } from './screens'
import './DeviceNetworkStatus.css'

/**
 * Fila Network Monitoring - opteemum/devices/details/networkStatus.php:
 * cardurile, pastilele de port, split, capete, istoricul de 24h, Throughput
 * si Latency. Watch la 15s, counts la 10s.
 */

function peerName(peer: NetworkPeer): string {
  if (peer.name) return peer.name
  if (peer.ip === '127.0.0.1' || peer.ip === '::1') return 'This machine'
  return ''
}

function PeerWho({ peer }: { peer: NetworkPeer }) {
  const name = peerName(peer)
  const who = name ? (
    <>
      <div className="nm-peer-name">{name}</div>
      <div className="nm-peer-ip">{peer.ip}</div>
    </>
  ) : (
    <div className="nm-peer-name">{peer.ip}</div>
  )
  if (peer.device_id > 0) {
    return (
      /* Fisa vecinului e ecran v4. Adresa veche era fila `deviceOverview` din
         portal, cu modulul de pornire in coada; in v4 Overview e chiar fila
         cu care se deschide fisa, deci ajunge `/app/inventoree/device/{id}`.
         Scrisa cum era, iesea din aplicatie ca sa fie intoarsa cu 302. */
      <a className="nm-peer-link" href={`/app/inventoree/device/${peer.device_id}`}>
        {who}
      </a>
    )
  }
  return who
}

function PeerRow({ peer, hidePort }: { peer: NetworkPeer; hidePort?: boolean }) {
  const n = peer.connections
  const countLabel = n === 1 ? '1 conn' : `${n} conn`
  const portLabel = (peer.dir === 'out' ? 'to :' : ':') + (peer.port || 0)
  return (
    <div className="nm-peer">
      <div className="nm-peer-who">
        <PeerWho peer={peer} />
      </div>
      <div className="nm-peer-meta">
        <div className="nm-peer-count">{countLabel}</div>
        {!hidePort && <div className="nm-peer-port">{portLabel}</div>}
      </div>
    </div>
  )
}

function PortPeers({ card, livePeers }: { card: NetworkPortCard; livePeers: NetworkPeer[] }) {
  const inbound = livePeers.filter((p) => p.dir !== 'out' && p.port === card.port)
  const listed = inbound.reduce((sum, p) => sum + (p.connections || 0), 0)
  const rest = Math.max(0, card.connections - listed)
  if (inbound.length) {
    return (
      <>
        {inbound.map((p, i) => (
          <PeerRow key={`${p.ip}:${p.port}:${i}`} peer={p} hidePort />
        ))}
        {rest > 0 && (
          <div className="nm-port-empty">
            {rest === 1
              ? '1 connection from this machine, or from an address that was not listed.'
              : `${rest} connections from this machine, or from addresses that were not listed.`}
          </div>
        )}
      </>
    )
  }
  return <div className="nm-port-empty">{card.unlisted_text}</div>
}

function PeersList({ peers }: { peers: NetworkPeer[] }) {
  const inbound = peers.filter((p) => p.dir !== 'out')
  const outbound = peers.filter((p) => p.dir === 'out')
  if (!inbound.length && !outbound.length) {
    return (
      <div className="alert alert-info" style={{ margin: 0 }}>
        <i className="fas fa-info-circle"></i> Waiting for the agent to report who is connected.
      </div>
    )
  }
  return (
    <>
      {inbound.length > 0 && (
        <div className="nm-peer-group">
          <p>Who is connected</p>
          {inbound.map((p, i) => (
            <PeerRow key={`in-${p.ip}:${p.port}:${i}`} peer={p} />
          ))}
        </div>
      )}
      {outbound.length > 0 && (
        <div className="nm-peer-group">
          <p>Where this talks</p>
          {outbound.map((p, i) => (
            <PeerRow key={`out-${p.ip}:${p.port}:${i}`} peer={p} />
          ))}
        </div>
      )}
    </>
  )
}

export function DeviceNetworkStatus({ id, open }: ScreenProps) {
  const page = useQuery(networkStatusQuery(id))
  const countsQ = useQuery({ ...networkCountsQuery(id), enabled: !!page.data })
  const connections = useQuery({ ...networkConnectionsQuery(id), enabled: !!page.data })
  const statistics = useQuery({ ...networkStatisticsQuery(id), enabled: !!page.data })
  const latency = useQuery({ ...networkLatencyQuery(id), enabled: !!page.data })
  const [openPort, setOpenPort] = useState<number | null>(null)
  const [openThroughput, setOpenThroughput] = useState(true)
  const [openLatency, setOpenLatency] = useState(true)

  useEffect(() => {
    if (!page.data) return
    const beat = () => {
      void networkWatch(id).catch(() => undefined)
    }
    beat()
    const t = window.setInterval(beat, 15_000)
    return () => window.clearInterval(t)
  }, [id, page.data])

  const counts = useMemo(() => {
    const live = countsQ.data
    if (live?.has) return live
    return page.data?.counts
  }, [countsQ.data, page.data])
  const peers = counts?.peers ?? page.data?.peers ?? []
  const rx = useMemo(() => {
    const dates = statistics.data?.dates
    if (!dates) return 0
    return Object.values(dates).reduce((sum, series) => sum + lastOf(series[0]), 0)
  }, [statistics.data])
  const tx = useMemo(() => {
    const dates = statistics.data?.dates
    if (!dates) return 0
    return Object.values(dates).reduce((sum, series) => sum + lastOf(series[1]), 0)
  }, [statistics.data])

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data || !counts) return <Spinner />

  const has = counts.has
  const total = counts.total
  const inbound = counts.inbound
  const outbound = counts.outbound
  const other = counts.other

  return (
    <div className="device-network-status">
      {has && (
        <div className="nm-cards">
          <div className="nm-card">
            <div className="nm-value" data-count="total">
              {total}
            </div>
            <div className="nm-label">Open connections</div>
          </div>
          <div className="nm-card is-in">
            <div className="nm-value" data-count="inbound">
              {inbound}
            </div>
            <div className="nm-label">Inbound</div>
          </div>
          <div className="nm-card is-out">
            <div className="nm-value" data-count="outbound">
              {outbound}
            </div>
            <div className="nm-label">Outbound</div>
          </div>
          <div className="nm-card is-other">
            <div className="nm-value" data-count="other">
              {other}
            </div>
            <div className="nm-label">Other states</div>
          </div>
        </div>
      )}

      <div id="nmTrio" className="nm-block">
        <h4>
          Open ports <span className="nm-when">outside localhost</span>
        </h4>
        {page.data.ports.length ? (
          <div className="nm-ports">
            {page.data.ports.map((card) => {
              const opened = openPort === card.port
              return (
                <div
                  key={card.port}
                  className={opened ? 'nm-port is-open' : 'nm-port'}
                  data-port={card.port}
                  data-connections={card.connections}
                >
                  <div className="nm-port-num" title={card.addresses.join(', ')}>
                    {card.port}
                  </div>
                  <div className={card.unknown ? 'nm-port-svc is-unknown' : 'nm-port-svc'}>{card.service}</div>
                  {card.process ? (
                    <a
                      className="nm-port-proc"
                      href={`/opteemum/runningApplications/${id}`}
                      data-process={card.process}
                      title={card.process}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        try {
                          sessionStorage.setItem('odNmAppFilter', card.process)
                        } catch {
                          /* private mode */
                        }
                        open('opteemum/runningApplications/')
                      }}
                    >
                      {card.process}
                    </a>
                  ) : (
                    <span className="nm-port-proc is-empty">&nbsp;</span>
                  )}
                  <button
                    type="button"
                    className="nm-port-conns is-toggle"
                    aria-expanded={opened}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setOpenPort(opened ? null : card.port)
                    }}
                  >
                    <span className="nm-port-conns-label">{card.connections_label}</span>
                    <i className="fas fa-chevron-right nm-port-chevron" aria-hidden="true"></i>
                  </button>
                  <div className="nm-port-peers">
                    <PortPeers card={card} livePeers={peers} />
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="alert alert-info" style={{ marginBottom: 16 }}>
            <i className="fas fa-info-circle"></i> No non-localhost listening ports reported yet.
          </div>
        )}

        <div className="nm-below">
          <div>
            <h4>Split</h4>
            <div className="nm-chart nm-chart-small" id="nmConnectionsChart">
              <NetworkPieChart inbound={inbound} outbound={outbound} other={other} />
            </div>
          </div>
          <div>
            <h4>Now</h4>
            <div className="nm-metrics">
              <div className="nm-metric">
                <div className="nm-value" data-count="total">
                  {total}
                </div>
                <div className="nm-label">Connections</div>
              </div>
              <div className="nm-metric">
                <div className="nm-value" id="nmTrafficRx">
                  {statistics.data?.dates ? `${bytesLabel(rx)}/s` : '—'}
                </div>
                <div className="nm-label">Received</div>
              </div>
              <div className="nm-metric">
                <div className="nm-value" id="nmTrafficTx">
                  {statistics.data?.dates ? `${bytesLabel(tx)}/s` : '—'}
                </div>
                <div className="nm-label">Sent</div>
              </div>
            </div>
            <div className="nm-peers" id="nmPeers">
              <PeersList peers={peers} />
            </div>
          </div>
        </div>
      </div>

      <div id="nmConnectionsTrend">
        {connections.isPending ? (
          <Spinner />
        ) : !connections.data?.categories?.length ? (
          connections.isError ? (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-circle"></i> Could not read the history.
            </div>
          ) : (
            <div className="alert alert-info">
              <i className="fas fa-info-circle"></i> No history yet - connection counts are kept every few minutes, so
              the line appears once the machine has reported a couple of times.
            </div>
          )
        ) : (
          <div className="nm-chart" id="nmTrendChart">
            <NetworkSplineChart
              categories={connections.data.categories}
              series={connections.data.series ?? []}
              title="Connections over the last 24 hours"
              yTitle="connections"
              format={(n) => String(n)}
            />
          </div>
        )}
      </div>

      <div
        className={openThroughput ? 'nm-section-head is-open' : 'nm-section-head'}
        data-target="nmStatistics"
        onClick={() => setOpenThroughput((v) => !v)}
      >
        <i className="fas fa-chevron-right nm-chevron"></i>
        <h4>Throughput</h4>
        <span className="nm-when">last 24 hours</span>
      </div>
      {openThroughput && (
        <div id="nmStatistics">
          <div className="statistics_charts_container">
            <ChartCards
              payload={statistics.data}
              loading={statistics.isPending}
              unit="Bytes"
              format={(n) => `${bytesLabel(n)}/s`}
              prefix="statistics_chart_"
            />
          </div>
        </div>
      )}

      <div
        className={openLatency ? 'nm-section-head is-open' : 'nm-section-head'}
        data-target="nmLatency"
        onClick={() => setOpenLatency((v) => !v)}
      >
        <i className="fas fa-chevron-right nm-chevron"></i>
        <h4>Latency</h4>
        <span className="nm-when">last 24 hours</span>
      </div>
      {openLatency && (
        <div id="nmLatency">
          <div className="network_latency_charts_container">
            <ChartCards
              payload={latency.data}
              loading={latency.isPending}
              unit="ms"
              format={(n) => `${Math.round(n * 10) / 10} ms`}
              prefix="latency_chart_"
            />
          </div>
        </div>
      )}
    </div>
  )
}

function ChartCards({
  payload,
  loading,
  unit,
  format,
  prefix,
}: {
  payload: { categories: string[]; dates?: Record<string, { name: string; data: number[] }[]> } | undefined
  loading: boolean
  unit: string
  format: (n: number) => string
  prefix: string
}) {
  if (loading) return <Spinner />
  if (!payload?.dates) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle"></i> Nothing to see yet...
      </div>
    )
  }
  return (
    <>
      {Object.entries(payload.dates).map(([name, series], i) => (
        <div className="nm-chart-card" key={name}>
          <div className="nm-chart-now">
            {series.map((s) => (
              <div className="nm-chart-now-item" key={s.name}>
                <div className="nm-now-value">{format(lastOf(s))}</div>
                <div className="nm-now-label">{s.name}</div>
              </div>
            ))}
          </div>
          <div className="nm-chart" data-chart={`${prefix}${i + 1}`}>
            <NetworkSplineChart categories={payload.categories} series={series} title={name} yTitle={unit} format={format} />
          </div>
        </div>
      ))}
    </>
  )
}
